import { type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { LibElement } from './lib-element';

/** Shared native-form lifecycle for checked controls. */
export abstract class CheckedFormElement extends LibElement {
  static readonly formAssociated = true;
  private readonly internals = typeof this.attachInternals === 'function' ? this.attachInternals() : undefined;

  @property({ type: Boolean, reflect: true }) checked = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  /** Native form field name. */
  @property({ reflect: true }) name = '';
  @state() private formDisabled = false;
  private defaultChecked = false;

  /** Includes disabled-fieldset state without changing the explicit disabled property. */
  get effectivelyDisabled(): boolean { return this.disabled || this.formDisabled; }

  protected revision = 0;
  protected get revisionEventName(): string | undefined { return undefined; }

  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'server-state']; }

  override attributeChangedCallback(name: string, previous: string | null, value: string | null): void {
    if (this.revisionEventName && name === 'server-state') {
      if (value !== null) {
        const state = JSON.parse(value) as { value: boolean; revision: number };
        if (state.revision >= this.revision) this.checked = state.value;
      }
      return;
    }
    if (this.revisionEventName && name === 'checked' && this.hasAttribute('server-state')) return;
    super.attributeChangedCallback(name, previous, value);
  }

  protected notifyChange(): void {
    if (this.revisionEventName)
      this.dispatchEvent(new CustomEvent(this.revisionEventName, {
        detail: { value: this.checked, revision: this.revision }, bubbles: true, composed: true,
      }));
    if (!this.revisionEventName || !this.hasAttribute('server-state'))
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  protected abstract get submissionValue(): string;

  protected override willUpdate(changed: PropertyValues<this>): void {
    super.willUpdate(changed);
    if (!this.hasUpdated) this.defaultChecked = this.checked;
    // Settle native disabled callbacks before render reads effectivelyDisabled.
    if (changed.has('disabled')) this.toggleAttribute('disabled', this.disabled);
    this.syncFormValue();
  }

  protected syncFormValue(): void {
    if (typeof this.internals?.setFormValue === 'function')
      this.internals.setFormValue(this.checked && !this.effectivelyDisabled ? this.submissionValue : null);
  }

  /** Applies disabled-fieldset state to both interaction and submission. */
  formDisabledCallback(disabled: boolean): void {
    this.formDisabled = disabled;
    this.syncFormValue();
    this.dispatchEvent(new Event('lib-disabled-change', { bubbles: true, composed: true }));
  }

  /** Restores the initial selection, independently of reflected checked attributes. */
  formResetCallback(): void {
    if (this.revisionEventName) this.revision++;
    const changed = this.checked !== this.defaultChecked;
    this.checked = this.defaultChecked;
    this.syncFormValue();
    if (changed) this.notifyReset();
  }

  protected notifyReset(): void {
    this.notifyChange();
  }
}
